import React, { useEffect, useState, useCallback } from 'react'
import './BouncingBall.css'

/**
 * A small basketball that occasionally bounces across the page.
 * Pure decoration — pointer-events: none, no impact on layout.
 *
 * Props:
 *  - intervalMin/intervalMax: random delay range between bounces (ms)
 *  - duration: how long each bounce animation lasts (ms)
 */
export default function BouncingBall({
  intervalMin = 30000,   // 30s minimum between bounces
  intervalMax = 90000,   // 90s maximum
}: {
  intervalMin?: number
  intervalMax?: number
}) {
  const [bouncing, setBouncing] = useState(false)
  const [direction, setDirection] = useState<'left' | 'right'>('right')

  const triggerBounce = useCallback(() => {
    setDirection(Math.random() > 0.5 ? 'right' : 'left')
    setBouncing(true)
  }, [])

  useEffect(() => {
    // First bounce after a short initial delay
    const firstTimeout = setTimeout(triggerBounce, 5000 + Math.random() * 10000)

    return () => clearTimeout(firstTimeout)
  }, [triggerBounce])

  useEffect(() => {
    if (!bouncing) {
      // Schedule the next bounce
      const delay = intervalMin + Math.random() * (intervalMax - intervalMin)
      const timer = setTimeout(triggerBounce, delay)
      return () => clearTimeout(timer)
    }
  }, [bouncing, intervalMin, intervalMax, triggerBounce])

  const handleAnimationEnd = () => {
    setBouncing(false)
  }

  if (!bouncing) return null

  return (
    <div
      className={`bb-container bb-${direction}`}
      onAnimationEnd={handleAnimationEnd}
    >
      <div className="bb-ball">
        <div className="bb-ball-inner" />
      </div>
    </div>
  )
}
